import { Box, Button, Divider, Grid, Stack, Switch, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateShopConfigApi } from '../../../redux/Account/action';
import { MuiTextFieldWithFocus, MuiTextFieldWithFocus40 } from '../../../config/MaterialUIConstants';

const PartsSettings = ({ fetchConfig, setShowLoader, setOpenSuccessModal, setSuccessMessage }) => {
    const dispatch = useDispatch();
    const shopConfigData = useSelector((state) => state.account.configDetailData);

    const [autoGeneratePartEnabled, setAutoGeneratePartEnabled] = useState(false);
    const [customPartEnabled, setCustomPartEnabled] = useState(false);
    const [prefix, setPrefix] = useState('');
    const [startNumber, setStartNumber] = useState('');

    // Store initial values for cancel functionality
    const [initialValues, setInitialValues] = useState({
        autoGeneratePartEnabled: false,
        customPartEnabled: false,
        prefix: '',
        startNumber: '',
    });

    // Single unified API update function
    const updateShopConfigData = async (customPayload = null) => {
        try {
            setShowLoader(true);

            const payload = customPayload || {
                appointmentReviewPlatform: shopConfigData?.appointmentReviewPlatform,
                inventory: {
                    part: {
                        autoGeneratePartNumber: autoGeneratePartEnabled,
                        customAutoGeneratePartEnabled: customPartEnabled,
                        autoGeneratePartNumberPrefix: prefix,
                        autoGeneratePartNumberPrefixOffset: startNumber ? Number(startNumber) : 0,
                    },
                },
            };

            const resp = await dispatch(updateShopConfigApi(payload));
            if (resp) {
                fetchConfig();
                setOpenSuccessModal(true);
                setSuccessMessage('Changes Saved');
            }
        } catch (error) {
            console.log('Error updating part settings:', error);
        } finally {
            setShowLoader(false);
        }
    };

    // Handle toggle switches
    const handleToggleChange = (toggleType, newValue) => {
        if (toggleType === 'autoGenerate') {
            setAutoGeneratePartEnabled(newValue);
            // Update API with opposite value for autoGeneratePartNumber
            const payload = {
                appointmentReviewPlatform: shopConfigData?.appointmentReviewPlatform,
                inventory: {
                    part: {
                        autoGeneratePartNumber: newValue, // Store opposite value
                        customAutoGeneratePartEnabled: customPartEnabled,
                        autoGeneratePartNumberPrefix: shopConfigData?.inventory?.part?.autoGeneratePartNumberPrefix,
                        autoGeneratePartNumberPrefixOffset: shopConfigData?.inventory?.part?.autoGeneratePartNumberPrefixOffset,
                    },
                },
            };
            updateShopConfigData(payload);
        } else if (toggleType === 'customPart') {
            setCustomPartEnabled(newValue);
            const payload = {
                appointmentReviewPlatform: shopConfigData?.appointmentReviewPlatform,
                inventory: {
                    part: {
                        autoGeneratePartNumber: autoGeneratePartEnabled, // Store opposite value
                        customAutoGeneratePartEnabled: newValue,
                        autoGeneratePartNumberPrefix: shopConfigData?.inventory?.part?.autoGeneratePartNumberPrefix,
                        autoGeneratePartNumberPrefixOffset: shopConfigData?.inventory?.part?.autoGeneratePartNumberPrefixOffset,
                    },
                },
            };
            updateShopConfigData(payload);
        }
    };

    // Handle cancel button click
    const handleCancelChanges = () => {
        setAutoGeneratePartEnabled(initialValues.autoGeneratePartEnabled);
        setCustomPartEnabled(initialValues.customPartEnabled);
        setPrefix(initialValues.prefix);
        setStartNumber(initialValues.startNumber);
    };
    const handleSaveChanges = () => {
        const payload = {
            appointmentReviewPlatform: shopConfigData?.appointmentReviewPlatform,
            inventory: {
                part: {
                    autoGeneratePartNumber: shopConfigData?.inventory?.part?.autoGeneratePartNumber,
                    customAutoGeneratePartEnabled: customPartEnabled,
                    autoGeneratePartNumberPrefix: prefix,
                    autoGeneratePartNumberPrefixOffset: startNumber ? Number(startNumber) : 0,
                },
            },
        };
        updateShopConfigData(payload);
    };

    // Initialize state from shopConfigData
    useEffect(() => {
        if (shopConfigData?.inventory?.part) {
            const partData = shopConfigData.inventory.part;

            setPrefix(partData.autoGeneratePartNumberPrefix || '');
            setAutoGeneratePartEnabled(partData.autoGeneratePartNumber || false);
            setCustomPartEnabled(partData.customAutoGeneratePartEnabled || false);

            // Convert number to string for UI display
            const offsetValue = partData.autoGeneratePartNumberPrefixOffset;
            setStartNumber(offsetValue ? offsetValue.toString() : '');

            setInitialValues({
                autoGeneratePartEnabled: partData.autoGeneratePartNumber || false,
                customPartEnabled: partData.customAutoGeneratePartEnabled || false,
                prefix: partData.autoGeneratePartNumberPrefix || '',
                startNumber: offsetValue ? offsetValue.toString() : '',
            });
        }
    }, [shopConfigData]);

    return (
        <Box
            sx={{
                boxShadow: 3,
                bgcolor: 'white',
                borderRadius: '10px',
                mt: 4,
                p: 3,
            }}
        >
            <Grid container spacing={2} justifyContent="space-between" alignItems="flex-start">
                {/* Left side - Label */}
                <Grid item xs={12} md={4}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                        Part Settings
                    </Typography>
                </Grid>

                {/* Right side - Text + Switch */}
                <Grid item xs={12} md={8}>
                    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                        <Box flex={1} minWidth={250}>
                            {/* <Stack direction="column" spacing={1}> */}
                            <Typography fontSize={20} fontWeight={500}>
                                Auto Generate Part Number
                            </Typography>
                            <Typography variant="body1" color="rgba(0, 0, 0, 0.87)">
                                If the part number field is left blank, an auto-generated number will be assigned. It can be edited later unless the part has
                                already been used on a customer invoice.
                            </Typography>
                            {/* </Stack> */}
                        </Box>
                        <Switch checked={autoGeneratePartEnabled} onChange={(e) => handleToggleChange('autoGenerate', e.target.checked)} color="primary" />
                    </Stack>

                    {autoGeneratePartEnabled && <Divider style={{ marginTop: '24px', marginBottom: '24px' }} />}
                    {/* --- New Section for Custom Part Numbering --- */}
                    {autoGeneratePartEnabled && (
                        <Box sx={{ marginTop: '-2px' }}>
                            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                                <Box flex={1} minWidth={250}>
                                    <Typography fontSize={20} fontWeight={500}>
                                        Custom Part Numbering
                                    </Typography>
                                </Box>
                                <Switch checked={customPartEnabled} onChange={(e) => handleToggleChange('customPart', e.target.checked)} color="primary" />
                            </Stack>
                            <Stack direction="row" spacing={3} mt={1} flexWrap="wrap">
                                <TextField
                                    disabled={!customPartEnabled}
                                    value={prefix}
                                    onChange={(e) => {
                                        const val = e.target.value.toUpperCase();
                                        const re = /^[A-Z]{0,3}$/;
                                        if (re.test(val)) {
                                            setPrefix(val);
                                        }
                                    }}
                                    sx={MuiTextFieldWithFocus}
                                    placeholder="e.g. APX"
                                />
                                <TextField
                                    disabled={!customPartEnabled}
                                    sx={MuiTextFieldWithFocus}
                                    value={startNumber}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, ''); // Remove non-digit characters
                                        if (/^\d{0,4}$/.test(val)) {
                                            // Allow up to 4 digits only
                                            setStartNumber(val);
                                        }
                                    }}
                                    placeholder="e.g. 1, 2, 500"
                                />
                            </Stack>
                            <Box mt={2}>
                                <Stack direction={'row'} alignItems={'center'} gap={1} justifyContent={'flex-start'} width={'100%'}>
                                    <div style={{ width: '8px', height: '8px', background: '#006CDD', borderRadius: '50%' }} />
                                    <Typography variant="body1" color="#000">
                                        Add a 3-letter prefix and choose the first sequence number.
                                        <br /> Example: If you enter APX + 1, you will get APX-0001, APX-0002, APX-0500.
                                    </Typography>
                                </Stack>
                                <Stack direction={'row'} alignItems={'center'} gap={1} justifyContent={'flex-start'} width={'100%'} mt={1.5}>
                                    <div style={{ width: '8px', height: '8px', background: '#006CDD', borderRadius: '50%' }} />
                                    <Typography variant="body1" color="#000">
                                        Changing only the start number later (before usage) will reset the text number in sequence.
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                    )}
                </Grid>

                {/* Action buttons */}
                <Grid container justifyContent="flex-end" mt={3}>
                    <Grid item>
                        <Stack spacing={2} direction="row">
                            <Button className="btn-text-size-manage" variant="outlined" color="primary" onClick={handleCancelChanges}>
                                Cancel
                            </Button>
                            <Button
                                disabled={!customPartEnabled}
                                className="btn-text-size-manage"
                                variant="contained"
                                color="primary"
                                onClick={handleSaveChanges}
                            >
                                Save
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PartsSettings;
